import { CaretRight, Check, X } from '@phosphor-icons/react';
import { Link, useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { AssistantLauncher } from '../assistant/AssistantLauncher';
import { INVOICE_V2_ROUTES } from '../constants/routes';
import { EmissionModeToggle } from './EmissionModeToggle';
import { useInvoiceTools } from '../hooks/useInvoiceTools';

export type WizardStep = 'conceptos' | 'pago' | 'revisar';

const STEPS: Array<{ id: WizardStep; label: string; shortLabel?: string; to: string }> = [
  { id: 'conceptos', label: 'Conceptos', to: INVOICE_V2_ROUTES.CONCEPTS },
  { id: 'pago', label: 'Pago', to: INVOICE_V2_ROUTES.PAYMENT },
  { id: 'revisar', label: 'Revisar y timbrar', shortLabel: 'Revisar', to: INVOICE_V2_ROUTES.REVIEW },
];

/**
 * Los tres pasos son navegables en ambos sentidos, incluidos los futuros: ir y
 * volver es el patrón normal, no la excepción — en el flujo actual la flecha de
 * regresar sale caliente en TODOS los pasos.
 */
const StepNav = ({ current }: { current: WizardStep }) => {
  const currentIndex = STEPS.findIndex((step) => step.id === current);

  return (
    <nav
      aria-label='Pasos de la factura'
      className='flex w-full items-center justify-between gap-0.5 sm:w-auto sm:justify-start sm:gap-1'>
      <Link
        to={INVOICE_V2_ROUTES.HOME}
        className='text-h-neutral-20 flex items-center rounded-full px-2 py-1.5 text-xs font-medium sm:px-3'>
        Inicio
      </Link>
      {STEPS.map((step, index) => {
        const isCurrent = index === currentIndex;
        const isCompleted = index < currentIndex;
        return (
          <div key={step.id} className='flex items-center gap-0.5 sm:gap-1'>
            <CaretRight size={12} className='text-h-neutral-80 flex-none sm:size-3.5' />
            <Link
              to={step.to}
              aria-current={isCurrent ? 'step' : undefined}
              className={cn(
                'flex items-center gap-1 rounded-full px-2 py-1.5 text-xs font-medium transition-colors duration-150 sm:gap-1.5 sm:px-3',
                isCurrent
                  ? 'bg-h-primary-90 text-h-primary-30'
                  : 'text-h-neutral-20 md:hover:bg-h-neutral-94',
              )}>
              {isCompleted ? (
                <Check size={13} weight='bold' className='text-h-success-50' />
              ) : (
                <span
                  className={cn(
                    'font-mono text-[10.5px]',
                    isCurrent ? 'opacity-75' : 'text-h-text-light',
                  )}>
                  {index + 1}
                </span>
              )}
              {step.shortLabel ? (
                <>
                  <span className='sm:hidden'>{step.shortLabel}</span>
                  <span className='hidden sm:inline'>{step.label}</span>
                </>
              ) : (
                step.label
              )}
            </Link>
          </div>
        );
      })}
    </nav>
  );
};

interface WizardBarProps {
  current: WizardStep;
  onExit: () => void;
}

export const WizardBar = ({ current, onExit }: WizardBarProps) => {
  const navigate = useNavigate();
  // Sin herramientas, en móvil la fila sería solo la X: ahí la salida es «Inicio», al inicio de los pasos.
  const tools = useInvoiceTools();

  return (
    <header className='border-h-neutral-90 bg-h-bg-app flex flex-col gap-2 border-b px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-4 sm:py-2.5 md:px-5'>
      <div className='flex min-w-0 items-center gap-3'>
        <span className='text-h-text-heading hidden flex-none text-[15px] font-semibold md:inline'>
          Nueva factura
        </span>
        <span className='bg-h-neutral-90 hidden h-[18px] w-px flex-none md:block' />
        <StepNav current={current} />
      </div>
      <div
        className={cn(
          'flex items-center justify-end gap-1.5 sm:flex-none sm:gap-2',
          !tools.any && 'hidden sm:flex',
        )}>
        <AssistantLauncher />
        <EmissionModeToggle />
        <button
          type='button'
          aria-label='Salir del flujo'
          onClick={() => {
            onExit();
            navigate(INVOICE_V2_ROUTES.HOME);
          }}
          className='text-h-neutral-20 md:hover:bg-h-neutral-94 md:hover:text-h-text-heading flex size-7 items-center justify-center rounded-lg transition-colors duration-150'>
          <X size={16} />
        </button>
      </div>
    </header>
  );
};
